/**
 * How old an observation is, in the words a pilot reads fastest and with the one
 * colour that is allowed to grade: neutral under an hour, amber at an hour, red
 * at ninety minutes — a METAR cycle plus grace.
 *
 * The age is ALWAYS printed beside the colour. Colour is emphasis here, never
 * the carrier: a card that only turned amber would tell a colour-blind pilot
 * nothing, and this is the number that decides whether the rest of the card is
 * worth reading at all.
 *
 * `now` is handed in rather than read: one clock ticks in the pane, so four
 * cards cannot disagree about what time it is.
 */
import { ageLevel, ageOf } from '../../autoload';
import { useT } from '../../i18n';
import { zTime } from '../../wx';
import { msOf } from './model';

/** Where a METAR stops being current, and where it stops being worth planning on. */
export const OBS_LIMITS = { warn: 60 * 60_000, bad: 90 * 60_000 };

export function ObsAge({ at, now }: { at: string | number | null | undefined; now: number }) {
  const t = useT();
  const ms = msOf(at);
  if (ms === null) return null;
  const age = ageOf(ms, now);
  const text =
    age.kind === 'now'
      ? t('fresh.now')
      : age.kind === 'min'
        ? t('fresh.ago', { n: age.mins })
        : /* past an hour a clock time is read faster than "vor 143 min", and the
             time an observation was MADE is what it is — not when it was read */
          t('fresh.obs', { time: zTime(new Date(ms).toISOString()) });
  const level = ageLevel(ms, now, OBS_LIMITS);
  return <span className={'wx-age' + (level ? ' ' + level : '')}>{text}</span>;
}
